import { Row, Col, Typography, Table, Button, message } from "antd";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelProcessingFifDsr,
  removeProcessingFifDsr,
  saveProcessingFifDsr,
} from "store/fif/fifDsrSlice";

const MESSAGE_KEY = "SAVE_FIF_DSR";

const SaveFifDsrPage = () => {
  useDocumentTitle("Save FIF DSR");
  const { processingFifDsr, status, error } = useAppSelector((state) => state.fifDsr);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingFifDsr;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingFifDsr());
      }
    }

    if (!Object.keys(processingFifDsr).length) {
      navigate(`/${routes.FIF.UPLOAD_DSR}`);
    }
  }, [dispatch, processingFifDsr, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "saving FIF DSR", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "FIF DSR saved successfully!",
        duration: 5,
        key: MESSAGE_KEY,
      });
      navigate(`/${routes.FIF.UPLOAD_DSR}`);
    } else if (status === "cancelling") {
      message.loading({
        content: "cancelling FIF DSR",
        key: MESSAGE_KEY,
      });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error, navigate]);

  let content;
  const { headerMapping } = processingFifDsr;
  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  const onSaveFifDsr = () => {
    dispatch(saveProcessingFifDsr());
  };

  const onCancelFifDsr = () => {
    dispatch(cancelProcessingFifDsr());
  };

  if (!Object.keys(processingFifDsr).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingFifDsr.successRows;
    const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = [
      ...Object.keys(headers).map(
        (key) =>
          ({
            key,
            title: headers[key],
            dataIndex: key,
          } as any)
      ),
    ];
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        rowKey="key"
        scroll={{ y: 500, x: "100vh" }}
      />
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save FIF DSR" />
        </Col>
      </Row>

      <Row>
        <Col>{content}</Col>
      </Row>

      {!!Object.keys(processingFifDsr).length && processingFifDsr.key && (
        <Row style={{ marginTop: "20px", gap: "10px" }}>
          <Col>
            <Button
              type="primary"
              onClick={onSaveFifDsr}
              loading={status === "loading"}
            >
              Save
            </Button>
          </Col>
          <Col>
            <Button
              onClick={onCancelFifDsr}
              loading={status === "cancelling"}
              danger
            >
              Cancel
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default SaveFifDsrPage;
